/**
 * A formula operand as the sender typed it — `"$1,200"`, `"15 %"`, `1200` —
 * as a number, or `undefined` when there is nothing numeric in it. An absent
 * or empty value is "not filled yet", never zero.
 *
 * Currency signs, thousands separators, units and whitespace are dropped
 * rather than failing the whole calculation: the number pill shows `$1,200`
 * on the deck exactly as filled in, so the total next to it has to count it.
 * Shared by every operand lookup (canvas pill, version save); the published
 * link's runtime (static-library `resolve-calc.ts`) applies the same rule.
 */
export const parseNumericOperand = (raw: unknown): number | undefined => {
  if (raw == null || raw === "") return undefined;
  if (typeof raw === "number") return Number.isNaN(raw) ? undefined : raw;
  const text = String(raw).trim();
  // A plain numeral first — this keeps `1e3` and `2e-3` meaning what they
  // say; stripping would read them as 13 and 2-3.
  const direct = Number(text);
  if (text && !Number.isNaN(direct)) return direct;
  // An exponent next to a sign or unit is not something to guess at.
  if (/\de/i.test(text)) return undefined;
  // ponytail: decimal point only — `1.234,56` (comma decimal) is not parsed.
  const cleaned = text.replace(/[^\d.-]/g, "");
  if (!/\d/.test(cleaned)) return undefined;
  const value = Number(cleaned);
  return Number.isNaN(value) ? undefined : value;
};
